import { createClient } from "@/utils/supabase/client";

const supabase = createClient();

/**
 * Uploads a file to the storage bucket and returns its public URL.
 *
 * If the file already exists, it will be replaced.
 *
 * @param bucket - The name of the bucket to upload the file to.
 * @param path - The path to upload the file to.
 * @param file - The file to upload.
 * @returns The public URL of the uploaded file.
 */
export const uploadFileToStorage = async (bucket: string, path: string, file: File) => {
  const { data, error } = await supabase.storage.from(bucket).upload(path, file, {
    upsert: true, // This ensures the file is replaced if it already exists
  });

  if (error) {
    throw new Error(error.message);
  }

  const {
    data: { publicUrl },
  } = supabase.storage.from(bucket).getPublicUrl(path);
  return publicUrl;
};

/**
 * Gets all public URLs from a folder in the storage bucket.
 *
 * @param bucket - The name of the bucket to get files from.
 * @param folderPath - The path to the folder containing files.
 * @returns An array of public URLs for all files in the folder.
 */
export const getAllPublicUrlsInFolder = async (bucket: string, folderPath: string) => {
  // List all files in the folder
  const { data: files, error } = await supabase.storage.from(bucket).list(folderPath);

  if (error) {
    throw new Error(`Error listing files in folder: ${error.message}`);
  }

  if (!files || files.length === 0) {
    return [];
  }

  // Folders to exclude from display (matching customer dashboard behavior)
  const excludedFolders = ["suggested_photos", "dining", "meeting_rooms"];

  // Filter out subfolders and excluded folders
  const imageFiles = files.filter((file) => {
    // Filter out folders - folders don't have metadata.size or have null metadata
    if (!file.metadata || file.metadata.size === undefined) return false;
    // Filter out excluded folder names
    if (excludedFolders.includes(file.name)) return false;
    // Filter out files in subfolders (name contains '/')
    if (file.name.includes("/")) return false;
    // Filter out placeholder files and hidden files
    if (file.name.startsWith(".") || file.name.includes(".emptyFolderPlaceholder")) return false;
    return true;
  });

  // Get public URLs for all files
  return imageFiles.map((file) => {
    const fullPath = folderPath ? `${folderPath}/${file.name}` : file.name;
    const {
      data: { publicUrl },
    } = supabase.storage.from(bucket).getPublicUrl(fullPath);
    return publicUrl;
  });
};

/**
 * Gets all signed URLs from a bucket.
 *
 * @param bucket - The name of the bucket to get files from.
 * @param paths - The paths to the files to get signed URLs for.
 * @param expiresIn - The number of seconds the signed URLs should be valid for.
 * @returns An array of signed URLs for all files.
 */
export const getAllSignedUrls = async (
  bucket: string,
  paths: string[],
  expiresIn: number = 60 * 60 * 30
): Promise<string[]> => {
  if (!paths.length) return [];

  const { data, error } = await supabase.storage.from(bucket).createSignedUrls(paths, expiresIn);

  if (error) {
    throw new Error(`Error getting signed URLs: ${error.message}`);
  }

  return data.map((entry) => entry.signedUrl).filter((url): url is string => typeof url === "string");
};

/**
 * Gets all file paths from a folder in the storage bucket.
 *
 * @param bucket - The name of the bucket to get files from.
 * @param folderPath - The path to the folder containing files.
 * @returns An array of file paths for all files in the folder.
 */
export const getAllFilePathsInFolder = async (bucket: string, folderPath: string) => {
  const { data: files, error } = await supabase.storage.from(bucket).list(folderPath);

  if (error) {
    throw new Error(`Error listing files in folder: ${error.message}`);
  }

  return files.map((file) => `${folderPath.replace(/\/+$/, "")}/${file.name}`);
};

/**
 * Delete files from the storage bucket.
 *
 * @param bucket - The name of the bucket to delete the file from.
 * @param paths - The paths to the files to delete.
 */
export const deleteFilesFromStorage = async (bucket: string, paths: string[]) => {
  const { data, error } = await supabase.storage.from(bucket).remove(paths);

  if (error) {
    throw new Error(error.message);
  }
};

/**
 * Copy files from one folder to another in the storage bucket.
 * Uses Supabase's server-side copy (no download/upload needed - much faster).
 *
 * @param bucket - The name of the bucket.
 * @param sourceFolder - The source folder path.
 * @param destFolder - The destination folder path.
 * @returns The number of files copied.
 */
export const copyFilesToFolder = async (bucket: string, sourceFolder: string, destFolder: string): Promise<number> => {
  // List all files in source folder
  const { data: files, error: listError } = await supabase.storage.from(bucket).list(sourceFolder);

  if (listError) {
    throw new Error(`Error listing files: ${listError.message}`);
  }

  if (!files || files.length === 0) {
    return 0;
  }

  // Filter out folders (only copy files)
  const filesToCopy = files.filter((file) => file.metadata && file.metadata.size !== undefined);

  let copiedCount = 0;

  for (const file of filesToCopy) {
    try {
      const sourcePath = `${sourceFolder}/${file.name}`;
      const destPath = `${destFolder}/${file.name}`;

      // Server-side copy (no download/upload needed)
      const { error: copyError } = await supabase.storage.from(bucket).copy(sourcePath, destPath);

      if (copyError) {
        console.error(`Error copying ${sourcePath} to ${destPath}:`, copyError);
        continue;
      }

      copiedCount++;
    } catch (error) {
      console.error(`Error copying file ${file.name}:`, error);
    }
  }

  return copiedCount;
};
